/**
 * Workflow variant API — the org's templated fact mappings for one workflow,
 * switchable on per duty.
 *
 * Every route is duty-scoped even though a variant belongs to the org: the duty
 * in the path is the ownership check, and it is what `enabled` is answered
 * against. Nova owns the data; Hoku proxies Nebula. See
 * hoku/server/handlers/duty_details/variants.py.
 */

import { AGENT_URL } from '@/config/env'
import { fetchJsonWithAuth } from '@/lib/api'
import { VariantSaveError } from '@/features/duties/types'
import { parseFactsSchema } from '@/features/workflows/facts'
import type {
  VariantFactBinding,
  WorkflowFactError,
  WorkflowVariant,
  WorkflowVariantsInfo,
} from '@/features/duties/types'

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function variantsUrl(dutyId: string, slug: string): string {
  return `${AGENT_URL}/data/duties/${dutyId}/workflows/${encodeURIComponent(slug)}/variants`
}

/**
 * Narrow one stored binding, or null if it isn't one we can render.
 *
 * A binding whose source we don't recognise, or whose payload is missing, is
 * not editable. `parseVariant` preserves it separately so an older client can
 * neither present it as a blank static value nor erase it on save. Non-null
 * static values are stringified: the modal edits them as text, while null stays
 * null because Nebula treats it as a real value distinct from an unbound fact.
 */
export function parseVariantBinding(raw: unknown): VariantFactBinding | null {
  if (!isRecord(raw)) return null
  switch (raw.source) {
    case 'static': {
      const { value } = raw
      if (value === undefined) return null
      if (value === null) return { source: 'static', value: null }
      if (
        typeof value !== 'string' &&
        typeof value !== 'number' &&
        typeof value !== 'boolean'
      ) {
        return null
      }
      return { source: 'static', value: String(value) }
    }
    case 'agentic':
      return typeof raw.instructions === 'string' && raw.instructions.trim()
        ? { source: 'agentic', instructions: raw.instructions }
        : null
    case 'catalogue':
      return typeof raw.binding === 'string' && raw.binding.trim()
        ? { source: 'catalogue', binding: raw.binding }
        : null
    default:
      return null
  }
}

export function parseVariantFacts(
  raw: unknown
): Record<string, VariantFactBinding> {
  if (!isRecord(raw)) return {}
  const facts: Record<string, VariantFactBinding> = {}
  for (const [name, value] of Object.entries(raw)) {
    const binding = parseVariantBinding(value)
    if (binding) facts[name] = binding
  }
  return facts
}

function unsupportedVariantFacts(raw: unknown): Record<string, unknown> {
  if (!isRecord(raw)) return {}
  const facts: Record<string, unknown> = {}
  for (const [name, value] of Object.entries(raw)) {
    if (!parseVariantBinding(value)) facts[name] = value
  }
  return facts
}

export function parseVariant(raw: unknown): WorkflowVariant | null {
  if (!isRecord(raw)) return null
  if (typeof raw.variant_key !== 'string' || !raw.variant_key) return null
  return {
    variant_key: raw.variant_key,
    title: typeof raw.title === 'string' ? raw.title : null,
    description: typeof raw.description === 'string' ? raw.description : null,
    // Only an explicit `false` deactivates, so a payload predating the field
    // reads as live rather than switched off everywhere.
    active: raw.active !== false,
    enabled: raw.enabled === true,
    duty_count: typeof raw.duty_count === 'number' ? raw.duty_count : 0,
    facts: parseVariantFacts(raw.facts),
    unsupported_facts: unsupportedVariantFacts(raw.facts),
    facts_schema: parseFactsSchema(raw.facts_schema),
  }
}

export async function fetchDutyWorkflowVariants(
  token: string,
  dutyId: string,
  slug: string,
  signal?: AbortSignal
): Promise<WorkflowVariantsInfo> {
  const raw = await fetchJsonWithAuth<unknown>({
    token,
    url: variantsUrl(dutyId, slug),
    signal,
    errorMessage: 'Failed to fetch workflow variants',
  })
  const obj = isRecord(raw) ? raw : {}
  const variants: WorkflowVariant[] = []
  if (Array.isArray(obj.variants)) {
    for (const item of obj.variants) {
      const variant = parseVariant(item)
      if (variant) variants.push(variant)
    }
  }
  return { variants }
}

/** Hoku keys binding errors by fact name: `{"note_body": "..."}`. */
function parseFactErrors(raw: unknown): WorkflowFactError[] {
  if (!isRecord(raw)) return []
  return Object.entries(raw).map(([field, message]) => ({
    field,
    message: typeof message === 'string' ? message : JSON.stringify(message),
  }))
}

/**
 * The org-wide variant write. Per-duty `enabled` is deliberately absent: it has
 * its own endpoint and must be committed with `useSetWorkflowVariantEnabled`.
 */
export type UpsertVariantPayload = {
  variantKey: string
  title: string
  description: string | null
  facts: Record<string, unknown>
  /**
   * The variant's own org-wide switch, distinct from the per-duty `enabled`.
   * Nova's upsert writes it on every save and defaults it to `true`, so an edit
   * has to send back the value it read — otherwise fixing a typo in a variant
   * that had been withdrawn org-wide would switch it back on for every duty.
   * Left off by the Add flow, where `true` is what a new note type means.
   */
  active?: boolean
  /**
   * Refuse the save if the key is already taken, instead of replacing what
   * holds it. Set by the Add flow, where the key is derived from a title the
   * user just typed and can land on a sibling's.
   */
  createOnly?: boolean
}

async function responseObject(
  response: Response
): Promise<Record<string, unknown>> {
  const payload: unknown = await response.json().catch(() => null)
  return isRecord(payload) ? payload : {}
}

function responseError(
  body: Record<string, unknown>,
  fallback: string,
  status: number
): string {
  return typeof body.error === 'string' ? body.error : `${fallback}: ${status}`
}

/**
 * Create or replace a variant.
 *
 * Idempotent on the variant key — the same call backs both the Add and Edit
 * flows — and `facts` replaces the stored bindings wholesale, so a mapping the
 * modal removed is really removed. Throws `VariantSaveError` carrying the
 * per-fact rejections so the modal can mark the offending rows.
 *
 * `createOnly` turns the upsert into a create for the Add flow: the variant is
 * the org's, so replacing one by key would rewrite the mappings every other
 * duty runs it with. Hoku answers 409 rather than overwriting.
 *
 * `active` is sent only when the caller has a value for it, since Nova writes
 * the column on every save — see the field's note on `UpsertVariantPayload`.
 */
export async function upsertDutyWorkflowVariant({
  token,
  dutyId,
  slug,
  variant,
}: {
  token: string
  dutyId: string
  slug: string
  variant: UpsertVariantPayload
}): Promise<WorkflowVariant> {
  const response = await fetch(variantsUrl(dutyId, slug), {
    method: 'POST',
    headers: { Authorization: token, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      variant_key: variant.variantKey,
      title: variant.title,
      description: variant.description,
      facts: variant.facts,
      // Omitted rather than defaulted, so a caller that has nothing to say about
      // it leaves the column to Nova instead of asserting a value here.
      ...(variant.active === undefined ? {} : { active: variant.active }),
      create_only: variant.createOnly === true,
    }),
  })

  const obj = await responseObject(response)

  if (!response.ok) {
    throw new VariantSaveError(
      responseError(obj, 'Failed to save workflow variant', response.status),
      parseFactErrors(obj.errors),
      response.status
    )
  }

  const saved = parseVariant(obj.variant)
  if (!saved) throw new VariantSaveError('Failed to save workflow variant')
  return saved
}

/**
 * Turn a variant on or off for this duty.
 *
 * This is the write that changes what Mercury can call on the duty; the variant
 * itself is untouched, so switching off here leaves it available to every other
 * duty that has it on.
 */
export async function setDutyWorkflowVariantEnabled({
  token,
  dutyId,
  slug,
  variantKey,
  enabled,
}: {
  token: string
  dutyId: string
  slug: string
  variantKey: string
  enabled: boolean
}): Promise<{ variantKey: string; enabled: boolean }> {
  const response = await fetch(
    `${variantsUrl(dutyId, slug)}/${encodeURIComponent(variantKey)}/enabled`,
    {
      method: 'POST',
      headers: { Authorization: token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    }
  )

  if (!response.ok) {
    const body = await responseObject(response)
    throw new Error(
      responseError(body, 'Failed to update workflow variant', response.status)
    )
  }

  return { variantKey, enabled }
}

/**
 * Delete a variant.
 *
 * Org-wide, not per duty: the variant belongs to the workflow, so this takes it
 * off every duty that had it on (Nova cascades the enablements). The caller is
 * expected to have confirmed that first.
 */
export async function deleteDutyWorkflowVariant({
  token,
  dutyId,
  slug,
  variantKey,
}: {
  token: string
  dutyId: string
  slug: string
  variantKey: string
}): Promise<{ variantKey: string }> {
  const response = await fetch(
    `${variantsUrl(dutyId, slug)}/${encodeURIComponent(variantKey)}`,
    {
      method: 'DELETE',
      headers: { Authorization: token },
    }
  )

  if (!response.ok) {
    const body = await responseObject(response)
    throw new Error(
      responseError(body, 'Failed to delete workflow variant', response.status)
    )
  }

  return { variantKey }
}
